<template>
  <div
    :id="`graph-editor-${graphEditor.id}`"
    class="editor"
  >
    <div class="editor__actions">
      <slot
        name="actions"
        :graph-editor="graphEditor"
      />
    </div>
    <GraphCanvas
      :graph-editor="graphEditor"
      :disabled="disabled"
    >
      <template #background>
        <slot name="background">
          <DefaultGraphBackground />
        </slot>
      </template>
      <template #default>
        <AdapterConnectionDraft :graph-editor="graphEditor" />
        <AdapterConnection
          v-for="connectionId of connectionIdSet"
          :key="connectionId"
          :graph-editor="graphEditor"
          :connection-id="connectionId"
        >
          <template
            #default="{
              connection, connectionData, connectionDisabled,
            }"
          >
            <slot
              name="connection"
              :connection="connection"
              :connection-data="connectionData"
              :connection-disabled="connectionDisabled"
            />
          </template>
        </AdapterConnection>
        <AdapterNode
          v-for="nodeId of nodeIdSet"
          :key="nodeId"
          :graph-editor="graphEditor"
          :node-id="nodeId"
          :disabled="disabled"
        >
          <template
            #default="{
              nodeData,
              nodeDisabled,
              isNodeFullyConnected,
              hasNodeConnections,
            }"
          >
            <slot
              name="node"
              :node-data="nodeData"
              :node-disabled="nodeDisabled"
              :is-node-fully-connected="isNodeFullyConnected"
              :has-node-connections="hasNodeConnections"
            />
          </template>
          <template
            #port="{
              port, portDisabled, connectionCount,
            }"
          >
            <slot
              name="port"
              :port="port"
              :port-disabled="portDisabled"
              :connection-count="connectionCount"
            />
          </template>
        </AdapterNode>
      </template>
    </GraphCanvas>
  </div>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  onBeforeUnmount, onMounted, shallowRef, triggerRef,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  IGraphEditorEvents,
  INodeSchemaRegistry, TEntityIdentifier,
} from '../types';

import AdapterConnectionDraft from './adapter-connection-draft.vue';
import AdapterConnection
  from './adapter-connection.vue';
import AdapterNode
  from './adapter-node.vue';
import DefaultGraphBackground
  from './defaults/default-graph-background.vue';
import GraphCanvas from './graph-canvas.vue';
import {
  ISlotPropsConnection,
  ISlotPropsNode,
  ISlotPropsPort, IUserInterfaceAdapterProps,
} from './types.ts';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
}>();

const nodeIdSet = shallowRef<Set<TEntityIdentifier>>(new Set(props.graphEditor.getAllNodes().map((node) => node.id)));
const connectionIdSet = shallowRef<Set<TEntityIdentifier>>(new Set(props.graphEditor.getAllConnections().map((connection) => connection.id)));

function onNodeCreated(event: IGraphEditorEvents['node:created']) {
  nodeIdSet.value.add(event.id);
  triggerRef(nodeIdSet);
}
function onNodeDeleted(event: IGraphEditorEvents<SCHEMA>['node:deleted']) {
  nodeIdSet.value.delete(event.id);
  triggerRef(nodeIdSet);
}

function onConnectionCreated(event: IGraphEditorEvents['connection:created']) {
  connectionIdSet.value.add(event.id);
  triggerRef(connectionIdSet);
}

function onConnectionDeleted(event: IGraphEditorEvents['connection:deleted']) {
  connectionIdSet.value.delete(event.id);
  triggerRef(connectionIdSet);
}

onMounted(() => {
  props.graphEditor.addEventListener('node:created', onNodeCreated);
  props.graphEditor.addEventListener('node:deleted', onNodeDeleted);
  props.graphEditor.addEventListener('connection:created', onConnectionCreated);
  props.graphEditor.addEventListener('connection:deleted', onConnectionDeleted);
});

onBeforeUnmount(() => {
  props.graphEditor.removeEventListener('node:created', onNodeCreated);
  props.graphEditor.removeEventListener('node:deleted', onNodeDeleted);
  props.graphEditor.removeEventListener('connection:created', onConnectionCreated);
  props.graphEditor.removeEventListener('connection:deleted', onConnectionDeleted);
});

defineSlots<{
  actions?(props: {
    graphEditor: GraphEditor<SCHEMA>,
  }): void,
  background?(): void,
  node?(props: ISlotPropsNode<SCHEMA>): void,
  port?(props: ISlotPropsPort): void,
  connection?(props: ISlotPropsConnection): void,
}>();
</script>

<style scoped lang="scss">
@use './styles';

.editor {
  position: relative;
  overflow: hidden;
  touch-action: none;

  &__actions {
    z-index: styles.$z-level-view-controls;
    display: inline-block;
    position: absolute;
    top: var(--gap-section);
    left: var(--gap-section);
    background: var(--surface-0);
  }
}
</style>
